/**
 * One search grammar for every search box in ClickPay.
 *
 * The browser half of backend/search_query.py. Both run the cases in
 * ./searchGrammarCases.json, so a query means the same thing whether the
 * server or the browser does the searching:
 *
 *  - case-insensitive substring match;
 *  - words separated by spaces must ALL appear somewhere in the row, in any
 *    column, in any order ("slime exact");
 *  - "double quotes" keep a phrase together ("glow in the dark");
 *  - a comma, semicolon or new line separates ALTERNATIVES, so a pasted list
 *    of ASINs or UIDs finds every one of them ("PB121, PB122");
 *  - a blank query matches everything.
 *
 * Not "|": campaign names are pipe-delimited, and pasting one must not split
 * it into an OR of its parts.
 */

/** Joins a row's fields. A control character no identifier contains, so a
 *  quoted phrase can never match across the boundary of two columns. */
export const FIELD_SEP = '\x1f'

/** A parsed query: alternatives, each a list of lower-cased terms that must
 *  all appear. `[]` = no search. */
export type ParsedSearch = string[][]

export function parseSearch(text: string | null | undefined): ParsedSearch {
  if (!text || !text.trim()) return []
  // Stash quoted phrases so a comma inside quotes is text, not a separator.
  // Private-use characters mark the stash: nothing anyone types contains them.
  const phrases: string[] = []
  const masked = text.replace(/"([^"]*)"/g, (_m, p: string) => {
    phrases.push(p)
    return `${phrases.length - 1}`
  })
  const out: ParsedSearch = []
  for (const alt of masked.split(/[,;\n\r]+/)) {
    const terms: string[] = []
    const re = /(\d+)|(\S+)/g
    let m: RegExpExecArray | null
    while ((m = re.exec(alt)) !== null) {
      if (m[1] !== undefined) {
        const phrase = phrases[Number(m[1])].trim().toLowerCase()
        if (phrase) terms.push(phrase)
      } else {
        for (const piece of m[2].split(/\d+/)) {
          const t = piece.trim().toLowerCase()
          if (t) terms.push(t)
        }
      }
    }
    if (terms.length) out.push(terms)
  }
  return out
}

/** Whether an already lower-cased haystack satisfies a parsed query. */
export function matchesParsed(haystack: string, parsed: ParsedSearch): boolean {
  if (parsed.length === 0) return true
  return parsed.some((alt) => alt.every((t) => haystack.includes(t)))
}

function scalarText(v: unknown): string | null {
  if (v == null) return null
  if (typeof v === 'string') return v
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : null
  if (typeof v === 'boolean') return String(v)
  return null
}

/** The lower-cased haystack for a list of field values. Arrays contribute
 *  each scalar element; objects and null contribute nothing. */
export function haystackOf(values: readonly unknown[]): string {
  const parts: string[] = []
  for (const v of values) {
    if (Array.isArray(v)) {
      for (const el of v) {
        const t = scalarText(el)
        if (t != null) parts.push(t)
      }
      continue
    }
    const t = scalarText(v)
    if (t != null) parts.push(t)
  }
  return parts.join(FIELD_SEP).toLowerCase()
}

/** Convenience: does `query` match these field values? */
export function matchesQuery(query: string, values: readonly unknown[]): boolean {
  const parsed = parseSearch(query)
  if (parsed.length === 0) return true
  return matchesParsed(haystackOf(values), parsed)
}
